import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { ExternalLink } from '@infl-ts/ui-advocate-components';
import { RA$ } from '@infl-ts/util-fp-ts';
import { useToggle } from '@infl-ts/util-hooks';
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Drawer, IconButton, TextField, Typography } from '@mui/material';
import { constNull, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { parse } from 'qs';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var hasThemeChangerParam = function hasThemeChangerParam(location) {
  return pipe(location.search, function (searchParams) {
    return parse(searchParams, {
      ignoreQueryPrefix: true
    });
  }, function (parsedParams) {
    return parsedParams['theme-changer'] !== undefined;
  });
};

var isJsonObject = function isJsonObject(theme) {
  return typeof theme === 'object' && theme !== null && !RA$.isArray(theme);
};

var parseThemeString = function parseThemeString(customTheme) {
  return pipe(O.tryCatch(function () {
    return JSON.parse(customTheme || '{}');
  }), O.chain(O.fromPredicate(isJsonObject)), O.getOrElseW(constNull));
};

var useThemeChanger = function useThemeChanger(isSuperAdmin) {
  var location = useLocation();

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      themeChangerEnabled = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      enableThemeChanger = _useToggle2$.on,
      disableThemeChanger = _useToggle2$.off;

  var _useState = useState({}),
      customTheme = _useState[0],
      setCustomTheme = _useState[1];

  var _useToggle3 = useToggle(),
      _useToggle4 = _slicedToArray(_useToggle3, 2),
      minimized = _useToggle4[0],
      toggleMinimized = _useToggle4[1].toggle;

  useEffect(function () {
    if (hasThemeChangerParam(location) && !themeChangerEnabled) {
      enableThemeChanger();
    }
  }, [location]);
  if (!isSuperAdmin || !themeChangerEnabled) return {
    themeChanger: null,
    customTheme: null
  };
  return {
    themeChanger: /*#__PURE__*/_jsx(Drawer, {
      id: "theme-changer",
      open: themeChangerEnabled,
      variant: "permanent",
      anchor: "bottom",
      PaperProps: {
        sx: {
          boxShadow: 8
        }
      },
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          m: 2,
          maxHeight: minimized ? 12 : 1000,
          transition: 'max-height 0.2s ease-in-out'
        },
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1
          },
          children: [/*#__PURE__*/_jsx(IconButton, {
            sx: {
              height: 20,
              width: 20,
              p: 0.25
            },
            size: "small",
            onClick: toggleMinimized,
            children: minimized ? /*#__PURE__*/_jsx(ExpandLess, {}) : /*#__PURE__*/_jsx(ExpandMore, {})
          }), /*#__PURE__*/_jsxs(Typography, {
            variant: "body2",
            children: ["Please use", ' ', /*#__PURE__*/_jsxs(ExternalLink, {
              sx: {
                fontSize: 'inherit'
              },
              target: "_blank",
              rel: "noopener noreferrer",
              href: "https://influitive.github.io/mui-theme-creator/",
              children: ["this theme creator", ' ']
            }), "to generate a theme."]
          }), /*#__PURE__*/_jsx(IconButton, {
            sx: {
              '& svg': {
                height: 20,
                width: 20
              },
              p: 0.25
            },
            size: "small",
            onClick: function onClick() {
              disableThemeChanger();
              setCustomTheme({});
            },
            children: /*#__PURE__*/_jsx(Close, {})
          })]
        }), /*#__PURE__*/_jsx(TextField, {
          sx: {
            width: 1
          },
          label: "Paste your Material UI theme object here",
          multiline: true,
          variant: "outlined",
          value: JSON.stringify(customTheme),
          onChange: function onChange(e) {
            return pipe(e.target.value, parseThemeString, setCustomTheme);
          },
          error: customTheme === null,
          helperText: customTheme === null ? 'Your custom theme is not in the correct format. Make sure there are no trailing commas, etc.' : ''
        })]
      })
    }),
    customTheme: customTheme
  };
};

export default useThemeChanger;